import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Sparkles, Play } from "lucide-react";
import { resolveFileUrl } from "../lib/urls";

/**
 * HighlightsTeaser — a 15-second auto-advancing slideshow of 8-10 AI-picked
 * best photos, embedded at the top of the guest gallery page.
 *
 * Props:
 *   slug   — public event slug
 *   onFull — optional callback when the user clicks "See full reel"
 */
const PHOTO_MS = 1500; // 10 photos * 1.5s = 15s

const HighlightsTeaser = ({ slug }) => {
  const [data, setData] = useState(null);
  const [idx, setIdx] = useState(0);
  const [hidden, setHidden] = useState(false);
  const [broken, setBroken] = useState(() => new Set());

  useEffect(() => {
    const base = (typeof window !== "undefined" && window.location?.origin) || "";
    axios.get(`${base}/api/public/events/${slug}/teaser`)
      .then(r => setData(r.data))
      .catch(() => setHidden(true));
  }, [slug]);

  const uploads = (data?.uploads || []).filter(u => !broken.has(u.id));
  const total = uploads.length;
  const safeIdx = total ? idx % total : 0;
  const advance = useCallback(() => setIdx(i => i + 1), []);

  useEffect(() => {
    if (!total) return;
    const t = setTimeout(advance, PHOTO_MS);
    return () => clearTimeout(t);
  }, [safeIdx, total, advance]);

  if (hidden || !data || total === 0) return null;
  const current = uploads[safeIdx];

  return (
    <section
      className="bg-white rounded-3xl soft-shadow overflow-hidden mb-4"
      data-testid="highlights-teaser"
    >
      <div className="relative aspect-[16/7] bg-navy-900 overflow-hidden">
        {uploads.map((u, i) => (
          <img
            key={u.id}
            src={resolveFileUrl(u.file_url)}
            alt=""
            loading={i <= 1 ? "eager" : "lazy"}
            onError={() => setBroken(prev => {
              const next = new Set(prev);
              next.add(u.id);
              return next;
            })}
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${i === safeIdx ? "opacity-100" : "opacity-0"}`}
          />
        ))}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent pointer-events-none" />

        <div className="absolute top-3 left-4 inline-flex items-center gap-1.5 bg-black/40 backdrop-blur text-white px-3 py-1 rounded-full text-[10px] uppercase tracking-widest font-bold">
          <Sparkles className="w-3 h-3" /> Teaser
        </div>

        <div className="absolute bottom-0 inset-x-0 p-4 flex items-end justify-between gap-3">
          <div className="text-white">
            <div className="font-serif italic text-xl md:text-2xl drop-shadow">{data.event_name}</div>
            <div className="text-xs text-white/70">{total} best moments · auto-playing</div>
          </div>
          <a
            href={`/g/${slug}/highlights`}
            data-testid="teaser-full-reel-btn"
            className="shrink-0 inline-flex items-center gap-1.5 bg-coral-300 hover:bg-coral-400 text-white text-xs font-semibold px-3 py-2 rounded-full"
          >
            <Play className="w-3.5 h-3.5" /> Full reel
          </a>
        </div>

        {/* Progress bar */}
        <div className="absolute bottom-0 inset-x-0 h-0.5 bg-white/15">
          <div
            className="h-full bg-coral-300 transition-all"
            style={{ width: `${((safeIdx + 1) / total) * 100}%` }}
          />
        </div>
      </div>
      {current?.uploader_name && (
        <div className="px-4 py-2 text-[11px] text-navy-700/60">
          Captured by <span className="font-semibold text-navy-700">{current.uploader_name}</span>
        </div>
      )}
    </section>
  );
};

export default HighlightsTeaser;
